import { FormField } from '@bonniernews/dn-design-system-web/assets/form-field/form-field.tsx'
import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import { ButtonToggle } from '@bonniernews/dn-design-system-web/components/button-toggle/button-toggle.tsx'

export interface TextInputProps extends SharedProps {
  /** It's used as name and id */
  name: string
  /** text, password, email, etc */
  type?:
    | 'color'
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'password'
    | 'search'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week'
  /**  A label for the input */
  label: string
  /** Used if error state shoud be set on load */
  error?: boolean
  /** What error message that should be displayed */
  errorMessage?: string
  required?: boolean
  /** Set to true if the field should be disabled */
  disabled?: boolean
  /** To use with the sites validation, some example values for dn-web: required, password, email */
  validation?: string
  /** A helptext for the field if needed */
  helpText?: string
  /** Fixed pixel value is used for typography to prevent scaling based on html font-size */
  forcePx?: boolean
  /** Set to true if field should have focus when loaded */
  autofocus?: boolean
  /** Ex. { data-prop: value } */
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/text-input](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/text-input)
 * - Storybook: [Form > TextInput](https://designsystem.dn.se/?path=/docs/basic-form-textinput--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use "@bonniernews/dn-design-system-web/components/text-input/text-input";`
 * ### Javascript
 * *The javascript is only needed for text fields of type password*
 *
 * ```javascript
 * import textInput from '@bonniernews/dn-design-system-web/components/text-input/text-input.js'
 * ```
 */
export const TextInput = ({
  name,
  type = 'text',
  label,
  error,
  errorMessage,
  validation,
  helpText,
  required,
  disabled,
  forcePx,
  autofocus,
  classNames = '',
  attributes,
}: TextInputProps) => {
  const componentClassName = 'ds-text-input'
  const wrapperClasses = formatClassString([componentClassName, forcePx && 'ds-force-px', classNames])
  const password = type === 'password'

  return (
    <FormField {...{ error, errorMessage, helpText, wrapperClasses }}>
      <input
        type={type}
        id={name}
        name={name}
        className={`${componentClassName}__input js-field-error ${password && 'password-toggle'}`}
        data-validation={validation}
        disabled={disabled}
        required={required}
        autoFocus={autofocus}
        placeholder=' '
        {...attributes}
      />

      <label className={`${componentClassName}__label`} htmlFor={name}>
        {label}
      </label>

      {password && (
        <ButtonToggle
          variant='transparent'
          size='sm'
          iconName='visibility'
          selectedIconName='visibility_off'
          isIconButton={true}
          selected={false}
          classNames={`${componentClassName}__password-toggle`}
          attributes={{
            id: `${name}-password-toggle`,
            'aria-label': 'Visa lösenord',
          }}
          forcePx={forcePx}
        />
      )}
    </FormField>
  )
}
